import { ReactElement, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Alert, Button, Heading, Paragraph, Skeleton } from '@digdir/designsystemet-react';
import { ArrowLeftIcon } from '@navikt/aksel-icons';
import type { Notified, WorkEnvironmentSurvey } from '../../api/workEnvironmentTypes';
import { useCompletePlanStepMutation } from '../../hooks/mutations/useWorkEnvironmentAssessmentMutations';
import { useActionPlanningQuery } from '../../hooks/queries/useActionPlanningQuery';
import { useSurveyActions } from '../../hooks/useSurveyActions';
import { questionLanguageFor } from '../FormSubmission/workEnvironmentFormView';
import { ProcessHero } from './components/ProcessHero';
import { AreaChoiceCard } from './plan/AreaChoiceCard';
import { AreaInfoCard } from './plan/AreaInfoCard';
import { AreasLeftNotice } from './plan/AreasLeftNotice';
import { DecisionsCard } from './plan/DecisionsCard';
import { PlanBuilder } from './plan/PlanBuilder';
import { PlanGuideCard } from './plan/PlanGuideCard';
import { DiscussionsCard } from './measures/DiscussionsCard';
import { PlanReviewCard } from './plan/PlanReviewCard';
import { PlanStatusCard } from './plan/PlanStatusCard';
import { PlanSummary } from './plan/PlanSummary';
import {
  approvalNotice,
  approvesAlone,
  completesPlanStep,
  editsPlan,
  focusId,
  isApproved,
  makesPlans,
  OpenPlan,
  openedPlan,
  openFor,
  PlanFocus,
  planHeading,
} from './planView';
import { AREA_PARAM, DECISION_PARAM } from './surveyRoutes';
import { currentStepIndex } from './surveyProcessView';
import process from './WorkEnvironmentSurveyProcess.module.css';
import classes from './WorkEnvironmentSurveyRisk.module.css';
import { ShareCard } from './components/ShareCard';
import { hasApprovedPlan, teamPageLink } from './sharingView';

type Props = {
  survey: WorkEnvironmentSurvey;
  /** Opens an area right away, as when the plan is changed from the follow-up */
  start?: { area: string; edit: boolean };
  /** Back to the page this was opened from */
  onBack?: () => void;
};

/**
 * Step 7: plans for one or more areas, and a decision for every other area at risk.
 * Approved plans can be changed, and are then approved again.
 */
export const WorkEnvironmentSurveyPlan = ({ survey, start, onBack }: Props): ReactElement => {
  const { t, i18n } = useTranslation();
  const { may } = useSurveyActions();
  const { data, isLoading, isError } = useActionPlanningQuery(survey.id, questionLanguageFor(i18n.language));
  const [params] = useSearchParams();
  // A link to an area left, as «?omrade=VIOLENCE», opens the step at that area
  const [focus, setFocus] = useState<PlanFocus | null>(() => {
    const key = start?.area ?? params.get(AREA_PARAM);
    return key ? { key, decision: params.get(AREA_PARAM) === key && params.get(DECISION_PARAM) === '1' } : null;
  });
  const [open, setOpen] = useState<OpenPlan | null>(() =>
    focus && !focus.decision ? { kind: 'area', key: focus.key } : null,
  );
  const [editing, setEditing] = useState(start?.edit ?? false);
  const [notified, setNotified] = useState<Notified>({});
  const complete = useCompletePlanStepMutation(survey.id);
  const opened = data ? openedPlan(data.areas, open) : null;
  const plans = Boolean(data && makesPlans(data, may('makePlan')));
  const editable = Boolean(data && opened && editsPlan(data, opened, editing, may('makePlan')));
  const notice = data && opened?.plan ? approvalNotice(opened.plan, data.role) : null;
  const areasLeft = data?.areasLeft ?? [];
  const close = () => {
    setOpen(null);
    setEditing(false);
    setNotified({});
  };
  /** From the notice of what is left: the plan of the area, or the area among those to choose */
  const choose = (key: string) => {
    const chosen = { key, decision: false };
    close();
    setFocus(chosen);
    setOpen(data ? openFor(chosen, data.areas) : null);
  };
  const showsAreas = Boolean(data) && !opened;
  const scrolledTo = useRef<PlanFocus | null>(null);
  useEffect(() => {
    if (!showsAreas || !focus || scrolledTo.current === focus) return;
    scrolledTo.current = focus;
    document.getElementById(focusId(focus))?.scrollIntoView?.({ block: 'center' });
  }, [showsAreas, focus]);

  return (
    <div data-size="md">
      <ProcessHero
        title={opened ? planHeading(opened.areas, t) : survey.title}
        intro={t(opened ? 'workEnvironmentPlan.builder.intro' : 'workEnvironmentPlan.intro')}
        tag={{
          label: t(data?.canChange === false ? 'workEnvironmentPlan.tag.done' : 'workEnvironmentPlan.tag.planning'),
          color: data?.canChange === false ? 'success' : 'info',
        }}
        process={{ current: currentStepIndex(survey.status), createdAt: survey.createdAt, responseDeadline: null }}
      />
      <div className={process.layout}>
        {isLoading && <Skeleton variant="rectangle" width="100%" height={320} />}
        {isError && (
          <Alert data-color="danger">
            <Paragraph>{t('workEnvironmentPlan.error')}</Paragraph>
          </Alert>
        )}
        {data && opened && (
          <>
            {editable ? (
              <PlanBuilder
                key={opened.plan?.id ?? 'new'}
                surveyId={survey.id}
                plan={opened.plan}
                areas={opened.areas}
                planning={data}
                onBack={close}
                onDone={(id, sent) => {
                  setOpen({ kind: 'plan', id });
                  setEditing(false);
                  setNotified(sent);
                }}
              />
            ) : (
              opened.plan && (
                <PlanSummary plan={opened.plan} areas={opened.areas} onBack={close}>
                  {/* Approving the plan does not wait for the other areas, but the team leader hears they are left */}
                  {notice && isApproved(notice) && plans && <AreasLeftNotice areas={areasLeft} onChoose={choose} />}
                  {notice && (
                    <PlanStatusCard
                      surveyId={survey.id}
                      plan={opened.plan}
                      notice={notice}
                      reviewMissing={data.reviewMissing}
                      notified={notified}
                      onBack={close}
                      onEdit={plans ? () => setEditing(true) : undefined}
                    />
                  )}
                </PlanSummary>
              )
            )}
            <div className={process.side}>
              {!approvesAlone(data) && (
                <PlanReviewCard surveyId={survey.id} plan={opened.plan} reviewMissing={data.reviewMissing} />
              )}
              {opened.areas.map((area) => (
                <AreaInfoCard key={area.key} surveyId={survey.id} area={area} named={opened.areas.length > 1} />
              ))}
            </div>
          </>
        )}
        {data && !opened && (
          <>
            <div className={classes.mainColumn}>
              {onBack && (
                <div>
                  <Button type="button" variant="tertiary" data-size="sm" onClick={onBack}>
                    <ArrowLeftIcon aria-hidden />
                    {t('workEnvironmentFollowUp.back')}
                  </Button>
                </div>
              )}
              {plans && hasApprovedPlan(data.areas) && <AreasLeftNotice areas={areasLeft} onChoose={choose} />}
              <AreaChoiceCard
                key={focus?.key}
                areas={data.areas}
                canChange={plans}
                focus={focus && !focus.decision ? focus.key : null}
                onOpenPlan={(id) => setOpen({ kind: 'plan', id })}
                onCreate={(keys) => setOpen({ kind: 'new', keys })}
              />
              <DecisionsCard
                surveyId={survey.id}
                areas={data.areas}
                canChange={plans}
                focus={focus?.decision ? focus.key : null}
              />
            </div>
            <div className={process.side}>
              {data.canChange && may('advance') && completesPlanStep(survey.status) && (
                <section aria-labelledby="plan-complete-heading" className={`${process.card} ${process.sideCard}`}>
                  <span className={`ds-label ${process.eyebrow}`} data-size="sm">
                    {t('workEnvironmentProcess.next.label')}
                  </span>
                  <Heading level={2} data-size="xs" id="plan-complete-heading">
                    {t('workEnvironmentPlan.complete.title')}
                  </Heading>
                  <Paragraph data-size="sm" className={process.subtle}>
                    {t(
                      data.canComplete ? 'workEnvironmentPlan.complete.ready' : 'workEnvironmentPlan.complete.notReady',
                    )}
                  </Paragraph>
                  {complete.isError && (
                    <Alert data-color="danger">
                      <Paragraph>{t('workEnvironmentPlan.complete.failed')}</Paragraph>
                    </Alert>
                  )}
                  {data.canComplete && (
                    <div>
                      <Button
                        variant="secondary"
                        data-size="sm"
                        loading={complete.isPending}
                        onClick={() => complete.mutate(undefined)}
                      >
                        {t('workEnvironmentPlan.complete.action')}
                      </Button>
                    </div>
                  )}
                </section>
              )}
              {hasApprovedPlan(data.areas) && (
                <ShareCard
                link={teamPageLink(window.location.origin, survey.uuid)}
                text={t('workEnvironmentSharing.plan')}
                linkLabel={t('workEnvironmentSharing.teamPage')}
                sharing={{ surveyId: survey.id, teamId: survey.teamId, kind: 'PLAN' }}
              />
              )}
              <DiscussionsCard surveyId={survey.id} />
              {plans && <PlanGuideCard />}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
